import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, Plus, X, Loader2, Users as UsersIcon, Pin, Trash2, ChevronLeft, ChevronDown, ChevronRight as ChevronRt, Search, Megaphone, Check, Paperclip } from "lucide-react";
import Avatar from "@/components/Avatar";
import PhotoUploader, { PhotoStrip } from "@/components/PhotoUploader";
import { useAuth } from "@/lib/auth";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const TYPE_META = {
  dm: { label: "Direct", color: "bg-slate-100 text-slate-600" },
  user_group: { label: "Groupe", color: "bg-blue-100 text-blue-700" },
  support_group: { label: "Support", color: "bg-emerald-100 text-emerald-700" },
  corner_group: { label: "Corner", color: "bg-purple-100 text-purple-700" },
};

export default function ChatPanel({ initialChatId = null, initialPrefill = "", initialRituelKey = null } = {}) {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showMulti, setShowMulti] = useState(false);
  const [multi, setMulti] = useState({ body: "", target: "all_corners", chat_ids: [], sector_id: "" });
  const [nu, setNu] = useState({ name: "", members: [] });
  const [userSearch, setUserSearch] = useState("");
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [rituelKey, setRituelKey] = useState(initialRituelKey);
  const feedRef = useRef(null);

  const canMultiPost = user && !user.is_corner && (user.role === "admin" || user.role === "director" || user.role === "manager" || user.role === "rt" || user.role === "mm" || user.is_support);

  const load = async () => {
    try {
      const { data } = await api.get("/chats");
      setChats(data);
    } catch { setChats([]); }
  };
  const loadMsgs = async (cid) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/chats/${cid}/messages`);
      setMessages(data);
      setTimeout(() => feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" }), 50);
    } catch (e) { setMessages([]); }
    setLoading(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  useEffect(() => { if (openId) loadMsgs(openId); }, [openId]);

  // Auto-open initialChatId + prefill text (from KPI Magasin rituel flow)
  useEffect(() => {
    if (initialChatId && !openId) setOpenId(initialChatId);
    if (initialPrefill && !text) setText(initialPrefill);
    // Pre-fill first name if user has one stored
    if (initialChatId && user?.is_corner && !firstName && user.first_name_norm) {
      setFirstName(user.first_name_norm.charAt(0).toUpperCase() + user.first_name_norm.slice(1));
    }
  }, [initialChatId, initialPrefill]);

  useEffect(() => {
    if (!showNew) return;
    api.get("/users/for-chat").then(({ data }) => setDirectoryUsers(data)).catch(() => setDirectoryUsers([]));
  }, [showNew]);

  const send = async (e) => {
    e?.preventDefault?.();
    if ((!text.trim() && attachments.length === 0) || !openId) return;
    if (user?.is_corner && !firstName.trim()) return toast.error("Prénom obligatoire");
    setSending(true);
    try {
      const endpoint = user?.is_corner ? `/chats/${openId}/messages/corner` : `/chats/${openId}/messages`;
      const payload = user?.is_corner
        ? { body: text.trim(), first_name: firstName.trim(), attachments, ...(rituelKey ? { rituel_key: rituelKey } : {}) }
        : { body: text.trim(), attachments };
      const { data } = await api.post(endpoint, payload);
      setMessages(m => [...m, data]);
      setText(""); setAttachments([]);
      if (rituelKey) { toast.success("Rituel validé ✓"); setRituelKey(null); }
      setTimeout(() => feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight, behavior: "smooth" }), 30);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSending(false);
  };

  const validateRoy = async (mid, actionKey) => {
    try {
      await api.post(`/chats/${openId}/messages/${mid}/validate`, { action_key: actionKey });
      toast.success("Merci !");
      loadMsgs(openId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitMulti = async () => {
    if (!multi.body.trim()) return toast.error("Message vide");
    try {
      const { data } = await api.post("/chats/multi-post", multi);
      toast.success(`Publié dans ${data.count} groupe(s)`);
      setShowMulti(false);
      setMulti({ body: "", target: "all_corners", chat_ids: [], sector_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createGroup = async () => {
    if (!nu.name.trim() || nu.members.length === 0) return toast.error("Nom + au moins un membre");
    try {
      const { data } = await api.post("/chats", { name: nu.name.trim(), members: nu.members });
      toast.success("Groupe créé"); setShowNew(false); setNu({ name: "", members: [] }); load(); setOpenId(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteChat = async (c) => {
    if (!confirm(`Supprimer « ${c.name} » ?`)) return;
    try { await api.delete(`/chats/${c.id}`); toast.success("Supprimé"); if (openId === c.id) setOpenId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startDm = async (uid) => {
    try {
      const { data } = await api.post(`/chats/dm/${uid}`);
      load(); setOpenId(data.id); setShowNew(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const supportGroups = chats.filter(c => c.type === "support_group");
  const cornerGroups = chats.filter(c => c.type === "corner_group");
  const otherChats = chats.filter(c => c.type !== "support_group" && c.type !== "corner_group");
  // Corner user's own group → pin at top of the corner list
  const myStoreId = user?.store_id;
  const myCornerGroup = myStoreId ? cornerGroups.find(c => c.store_id === myStoreId) : null;
  // For corner users: "same team" = same sector_zone OR same BM (excluding self)
  const myPeersGroup = myCornerGroup
    ? cornerGroups.filter(c => c.store_id !== myStoreId && (
        (c.sector_zone && c.sector_zone === myCornerGroup.sector_zone) ||
        (c.bm_name && c.bm_name === myCornerGroup.bm_name)))
    : [];
  const myPeerIds = new Set(myPeersGroup.map(c => c.id));
  const otherCornerGroups = cornerGroups.filter(c =>
    c.store_id !== myStoreId && !myPeerIds.has(c.id));
  const current = chats.find(c => c.id === openId);
  const filteredDir = directoryUsers.filter(u => !userSearch.trim() || (u.name || "").toLowerCase().includes(userSearch.toLowerCase()));
  // Group corner_groups by Region → Sector zone (source of truth = store fiche)
  const cornerTree = otherCornerGroups.reduce((acc, c) => {
    const reg = c.region_name || c.dr_name || "Sans région";
    const sec = c.sector_zone || c.sector_name || "Sans secteur";
    if (!acc[reg]) acc[reg] = {};
    if (!acc[reg][sec]) acc[reg][sec] = [];
    acc[reg][sec].push(c);
    return acc;
  }, {});
  const [foldState, setFoldState] = useState({});
  const [cornerSearch, setCornerSearch] = useState("");

  const roleLabel = (u) => u.is_support ? "Support" : u.role === "director" ? "DR" : u.role === "manager" ? "BM" : u.role === "admin" ? "Admin" : u.role;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" style={{ maxHeight: 720 }} data-testid="chat-panel">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 py-3 text-white flex items-center gap-2">
        {openId && <button onClick={() => setOpenId(null)} data-testid="chat-back" className="w-6 h-6 grid place-items-center rounded hover:bg-white/10"><ChevronLeft className="w-4 h-4"/></button>}
        <MessageCircle className="w-4 h-4"/>
        <div className="font-display font-bold text-sm truncate">{current ? (current.dm_name || current.name) : "Messagerie"}</div>
        {!openId && (
          <div className="ml-auto flex items-center gap-1">
            {canMultiPost && (
              <button onClick={() => setShowMulti(true)} data-testid="chat-multipost-btn" className="text-xs font-bold px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 flex items-center gap-1" title="Publier dans plusieurs groupes">
                <Megaphone className="w-3 h-3"/>
              </button>
            )}
            <button onClick={() => setShowNew(true)} data-testid="chat-new-btn" className="text-xs font-bold px-2 py-1 rounded bg-white/15 hover:bg-white/25 flex items-center gap-1">
              <Plus className="w-3 h-3"/> Nouveau
            </button>
          </div>
        )}
        {openId && current && current.type === "user_group" && current.created_by === user?.id && (
          <button onClick={() => deleteChat(current)} data-testid="chat-delete" className="ml-auto text-xs font-bold px-2 py-1 rounded bg-rose-500 hover:bg-rose-600 flex items-center gap-1">
            <Trash2 className="w-3 h-3"/>
          </button>
        )}
      </div>

      {!openId && !showNew && !showMulti && (
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {/* Corner user's own group — always shown first at the top */}
          {myCornerGroup && (
            <div className="border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-1" data-testid="my-corner-group-block">
              <div className="text-[10px] uppercase font-bold text-amber-700 px-2 py-1 flex items-center gap-1">
                <Pin className="w-3 h-3"/> Mon corner
              </div>
              <ChatRow c={myCornerGroup} onOpen={() => setOpenId(myCornerGroup.id)} me={user?.id}/>
            </div>
          )}
          {/* Corner user — Corners du même secteur / rattachés au même BM */}
          {myCornerGroup && myPeersGroup.length > 0 && (
            <div className="border border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl p-1" data-testid="my-peers-group-block">
              <div className="text-[10px] uppercase font-bold text-blue-700 px-2 py-1 flex items-center gap-1">
                <UsersIcon className="w-3 h-3"/> Corners de mon équipe · {myCornerGroup.sector_zone || myCornerGroup.bm_name}
              </div>
              {myPeersGroup
                .slice()
                .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"))
                .map(c => <ChatRow key={c.id} c={c} onOpen={() => setOpenId(c.id)} me={user?.id}/>)}
            </div>
          )}
          {supportGroups.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 px-2 py-1 flex items-center gap-1">
                <Pin className="w-3 h-3"/> Groupes support épinglés
              </div>
              {supportGroups.map(c => <ChatRow key={c.id} c={c} onOpen={() => setOpenId(c.id)}/>)}
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-500 px-2 py-1">Messages & groupes</div>
            {otherChats.length === 0 && otherCornerGroups.length === 0 && !myCornerGroup && <div className="text-xs text-slate-400 italic p-4 text-center">Aucune conversation — cliquez sur Nouveau.</div>}
            {otherChats.map(c => <ChatRow key={c.id} c={c} onOpen={() => setOpenId(c.id)} onDelete={() => deleteChat(c)} me={user?.id}/>)}
          </div>
          {otherCornerGroups.length > 0 && (
            <div>
              <div className="flex flex-wrap items-center gap-2 px-2 py-2 border-b border-slate-100 bg-slate-50/60">
                <div className="text-[10px] uppercase font-bold text-slate-500 basis-full sm:basis-auto sm:flex-1">Corners · {cornerGroups.length}</div>
                <div className="relative w-full sm:w-40">
                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <input value={cornerSearch} onChange={e => setCornerSearch(e.target.value)} data-testid="corner-search"
                         placeholder="Filtrer corner ou ville…"
                         className="w-full pl-6 pr-2 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-blue-400"/>
                </div>
              </div>
              {Object.entries(cornerTree).sort(([a], [b]) => a.localeCompare(b)).map(([reg, secs]) => {
                const drKey = `reg:${reg}`;
                const drOpen = cornerSearch.trim() ? true : (foldState[drKey] !== false);
                const drCount = Object.values(secs).reduce((s, arr) => s + arr.length, 0);
                return (
                  <div key={reg} className="mb-1">
                    <button onClick={() => setFoldState(f => ({...f, [drKey]: !drOpen}))}
                            data-testid={`fold-region-${reg}`}
                            className="w-full flex items-center gap-1.5 px-2 py-2 sm:py-1 rounded hover:bg-slate-100 active:bg-slate-200 text-left touch-manipulation">
                      {drOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0"/> : <ChevronRt className="w-3.5 h-3.5 text-slate-500 shrink-0"/>}
                      <span className="text-[12px] sm:text-[11px] font-bold text-slate-700 flex-1 truncate">{reg}</span>
                      <span className="text-[10px] text-slate-400 shrink-0">{drCount}</span>
                    </button>
                    {drOpen && Object.entries(secs).sort(([a], [b]) => a.localeCompare(b)).map(([sec, list]) => {
                      const secKey = `sec:${reg}:${sec}`;
                      const secOpen = cornerSearch.trim() ? true : (foldState[secKey] !== false);
                      const visible = list.filter(c => !cornerSearch.trim() || (c.name || "").toLowerCase().includes(cornerSearch.toLowerCase()) || (c.ville || "").toLowerCase().includes(cornerSearch.toLowerCase()));
                      if (cornerSearch.trim() && visible.length === 0) return null;
                      return (
                        <div key={sec} className="ml-3 sm:ml-4">
                          <button onClick={() => setFoldState(f => ({...f, [secKey]: !secOpen}))}
                                  data-testid={`fold-sec-${sec}`}
                                  className="w-full flex items-center gap-1.5 px-2 py-1.5 sm:py-0.5 rounded hover:bg-slate-50 active:bg-slate-100 text-left touch-manipulation">
                            {secOpen ? <ChevronDown className="w-3 h-3 text-slate-400 shrink-0"/> : <ChevronRt className="w-3 h-3 text-slate-400 shrink-0"/>}
                            <span className="text-[11px] sm:text-[10px] font-semibold text-slate-600 flex-1 truncate">{sec}</span>
                            <span className="text-[10px] text-slate-400 shrink-0">{list.length}</span>
                          </button>
                          {secOpen && visible.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" })).map(c => <ChatRow key={c.id} c={c} onOpen={() => setOpenId(c.id)} me={user?.id}/>)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {cornerSearch.trim() && cornerGroups.filter(c => (c.name || "").toLowerCase().includes(cornerSearch.toLowerCase()) || (c.ville || "").toLowerCase().includes(cornerSearch.toLowerCase())).length === 0 && (
                <div className="p-4 text-xs text-slate-400 italic text-center">Aucun corner ne correspond à « {cornerSearch} »</div>
              )}
            </div>
          )}
        </div>
      )}

      {!openId && showMulti && (
        <div className="flex-1 overflow-y-auto p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold text-sm flex items-center gap-1"><Megaphone className="w-4 h-4 text-amber-500"/> Publier dans plusieurs groupes</div>
            <button onClick={() => setShowMulti(false)}><X className="w-4 h-4 text-slate-500"/></button>
          </div>
          <div className="text-[10px] uppercase font-bold text-slate-500 py-1">Cible</div>
          <SearchSelect value={multi.target} onChange={v => setMulti({...multi, target: v})} options={[{ value: "all_corners", label: "Tous les corners (mes groupes corner)" }, { value: "all_groups", label: "Tous mes groupes (corner + user + support)" }, { value: "chats", label: "Sélection manuelle" }]} data-testid="multi-target" size="lg" menuWidth={260}/>
          {multi.target === "chats" && (
            <div className="border border-slate-200 rounded-lg p-2 max-h-40 overflow-y-auto mb-2 space-y-0.5">
              {chats.filter(c => c.type !== "dm").map(c => {
                const on = multi.chat_ids.includes(c.id);
                return (
                  <label key={c.id} className={`flex items-center gap-2 p-1 rounded text-xs cursor-pointer ${on ? "bg-amber-50" : "hover:bg-slate-50"}`}>
                    <input type="checkbox" checked={on} onChange={() => setMulti(p => ({...p, chat_ids: on ? p.chat_ids.filter(x => x !== c.id) : [...p.chat_ids, c.id]}))} className="w-3 h-3"/>
                    <span className="truncate">{c.name}</span>
                  </label>
                );
              })}
            </div>
          )}
          <textarea rows={4} value={multi.body} onChange={e => setMulti({...multi, body: e.target.value})} data-testid="multi-body"
                    placeholder="Votre message aux équipes…" className="w-full px-2 py-2 rounded-lg border border-slate-200 text-xs mb-2"/>
          <button onClick={submitMulti} data-testid="multi-send"
                  className="w-full py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold flex items-center justify-center gap-1">
            <Send className="w-3 h-3"/> Publier
          </button>
        </div>
      )}

      {!openId && !showNew && !showMulti && !openId && false && null /* legacy hint */}

      {!openId && showNew && (
        <div className="flex-1 overflow-y-auto p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-bold text-sm">Nouvelle conversation</div>
            <button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-slate-500"/></button>
          </div>
          <div className="relative mb-2">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={userSearch} onChange={e => setUserSearch(e.target.value)} data-testid="chat-user-search"
                   placeholder="Rechercher un utilisateur…" className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 text-xs"/>
          </div>
          <div className="text-[10px] uppercase font-bold text-slate-500 py-1">Discussion directe</div>
          <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
            {filteredDir.slice(0, 30).map(u => (
              <button key={u.id} onClick={() => startDm(u.id)} data-testid={`start-dm-${u.id}`} className="w-full flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 text-left">
                <Avatar user={u} size={26}/>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-800 truncate">{u.name}</div>
                  <div className="text-[10px] text-slate-500">{roleLabel(u)}</div>
                </div>
                <Send className="w-3.5 h-3.5 text-slate-400"/>
              </button>
            ))}
          </div>
          <div className="text-[10px] uppercase font-bold text-slate-500 py-1">Créer un groupe</div>
          <input value={nu.name} onChange={e => setNu({...nu, name: e.target.value})} data-testid="chat-new-group-name"
                 placeholder="Nom du groupe" className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs mb-2"/>
          <div className="text-[10px] uppercase font-bold text-slate-500 py-1">Membres · {nu.members.length}</div>
          <div className="space-y-1 max-h-40 overflow-y-auto mb-2 border border-slate-100 rounded-lg p-1.5">
            {filteredDir.slice(0, 40).map(u => {
              const on = nu.members.includes(u.id);
              return (
                <label key={u.id} className={`flex items-center gap-2 p-1 rounded cursor-pointer text-xs ${on ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                  <input type="checkbox" checked={on} onChange={() => setNu(p => ({...p, members: on ? p.members.filter(x => x !== u.id) : [...p.members, u.id]}))} className="w-3 h-3"/>
                  <Avatar user={u} size={20}/>
                  <span className="flex-1 text-slate-800 truncate">{u.name}</span>
                  <span className="text-[9px] text-slate-400 uppercase font-bold">{roleLabel(u)}</span>
                </label>
              );
            })}
          </div>
          <button onClick={createGroup} data-testid="chat-create-group"
                  className="w-full py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold">Créer le groupe</button>
        </div>
      )}

      {openId && (
        <>
          {rituelKey && (
            <div className="bg-gradient-to-r from-amber-100 to-orange-100 border-b border-amber-300 px-3 py-2 flex items-center justify-between gap-2" data-testid="rituel-banner">
              <div className="text-[11px] font-semibold text-amber-900">
                🎯 Rituel en cours : <b>{rituelKey.replaceAll("_", " ")}</b>
                <div className="text-[10px] font-normal text-amber-800 mt-0.5">Envoyez votre message pour valider le rituel du jour</div>
              </div>
              <button data-testid="rituel-cancel" onClick={() => setRituelKey(null)}
                      className="text-[10px] font-bold text-amber-700 hover:text-amber-900 px-2 py-1 rounded hover:bg-white/50">
                Annuler
              </button>
            </div>
          )}
          <div ref={feedRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
            {loading && <div className="text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline"/></div>}
            {!loading && messages.length === 0 && <div className="text-xs text-slate-400 italic text-center py-6">Aucun message — commencez la discussion.</div>}
            {messages.map(m => {
              const mine = m.sender_id === user?.id;
              const isRoy = m.is_bot || m.sender_id === "roy-bot";
              const validated = m.validated || {};
              return (
                <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.id}`}>
                  <Avatar user={{ id: m.sender_id, name: m.sender_name, avatar_url: m.sender_avatar }} size={26}/>
                  <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-xs ${mine ? "bg-blue-600 text-white rounded-br-sm" : isRoy ? "bg-gradient-to-br from-orange-50 to-amber-50 border border-amber-300 rounded-bl-sm text-slate-800" : "bg-white border border-slate-200 rounded-bl-sm text-slate-800"}`}>
                    {!mine && <div className={`text-[10px] font-bold mb-0.5 ${isRoy ? "text-amber-700" : "opacity-70"}`}>{m.sender_name}</div>}
                    <div className="whitespace-pre-wrap">{m.body}</div>
                    {(m.attachments || []).length > 0 && (
                      <div className="mt-2"><PhotoStrip paths={m.attachments}/></div>
                    )}
                    {(m.actions || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {m.actions.map(a => {
                          const done = validated[a.key];
                          return (
                            <button key={a.key} onClick={() => validateRoy(m.id, a.key)}
                                    disabled={!!done}
                                    data-testid={`roy-btn-${m.id}-${a.key}`}
                                    className={`text-[10px] font-bold px-2 py-1 rounded-lg ${done ? "bg-emerald-600 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}`}>
                              {done ? <><Check className="w-2.5 h-2.5 inline mr-0.5"/> Envoyé par {done.by_user_name?.split(" ")[0]}</> : a.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className={`text-[9px] mt-1 ${mine ? "text-blue-100" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={send} className="p-2 border-t border-slate-200 bg-white">
            {attachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {attachments.map((a, i) => (
                  <div key={i} className="relative">
                    <img src={a} alt="" className="w-14 h-14 object-cover rounded-lg border border-slate-200"/>
                    <button type="button" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                            data-testid={`chat-remove-attach-${i}`}
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white grid place-items-center text-[9px]">×</button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-end">
              {user?.is_corner && (
                <input value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="corner-first-name"
                       placeholder="Prénom*" className="w-24 px-2 py-2 rounded-xl border border-amber-300 bg-amber-50 text-xs font-semibold"/>
              )}
              <div className="flex-1 flex items-center gap-1">
                <PhotoUploader value={attachments} onChange={setAttachments} testid="chat-attach" max={4} compact/>
                <input value={text} onChange={e => setText(e.target.value)} data-testid="chat-input"
                       placeholder="Votre message…" className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-xs"/>
              </div>
              <button type="submit" disabled={sending || (!text.trim() && attachments.length === 0) || (user?.is_corner && !firstName.trim())} data-testid="chat-send"
                      className="px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50">
                {sending ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

function ChatRow({ c, onOpen, onDelete, me }) {
  const last = c.last_message;
  const type = TYPE_META[c.type] || TYPE_META.dm;
  const preview = last ? `${last.sender_id === me ? "Vous" : (last.sender_name || "?").split(" ")[0]}: ${last.body || ""}` : "Aucun message";
  return (
    <button onClick={onOpen} data-testid={`chat-row-${c.id}`}
            className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 text-left group">
      <div className={`w-9 h-9 rounded-full grid place-items-center text-sm shrink-0 ${c.type === "support_group" ? "shadow-inner" : "bg-slate-100"}`}
           style={c.type === "support_group" ? { backgroundColor: (c.color || "#2563eb") + "22", color: c.color } : {}}>
        {c.type === "support_group" ? <span>{c.emoji || "💬"}</span>
         : c.type === "dm" ? <Avatar user={{ name: c.dm_name || c.name, avatar_url: c.dm_avatar }} size={32}/>
         : <UsersIcon className="w-4 h-4 text-slate-500"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <div className="text-xs font-semibold text-slate-800 truncate">{c.type === "dm" ? (c.dm_name || c.name) : c.name}</div>
          <span className={`text-[9px] font-bold px-1 rounded ${type.color}`}>{type.label}</span>
          {c.pinned && <Pin className="w-2.5 h-2.5 text-amber-500"/>}
        </div>
        <div className="text-[10px] text-slate-500 truncate">{preview}</div>
      </div>
      {c.unread_count > 0 && <span className="bg-rose-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5">{c.unread_count}</span>}
      {onDelete && c.created_by === me && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 p-1">
          <Trash2 className="w-3 h-3"/>
        </button>
      )}
    </button>
  );
}
